import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { FilterMetadata, SelectItem } from '@openng/optimus-ui/api';
import { interval, Subscription } from 'rxjs';
import { MC_ST_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { ShiftTurnover } from 'src/app/model/shift/shift-turnover';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { ShiftTurnoverBuilderService } from 'src/app/services/builder/shift-turnover-builder.service';
import { ShiftTurnoverTableService } from 'src/app/services/table/shift-turnover-table.service';
import { getShiftTurnoverColumns } from '../definitions/shift-turnover-table-definition';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ShiftGroup } from 'src/app/model/shift/shift-group';
import { ShiftTurnoverSearch } from 'src/app/model/shift/shift-turnover-search-results';

@Component({
  selector: 'app-mc-shift-turnover-container',
  templateUrl: './mc-shift-turnover-container.component.html',
  styleUrls: ['./mc-shift-turnover-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    DatePicker,
    FormsModule,
    ButtonDirective,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class McShiftTurnoverContainerComponent
  extends SearchContainerComponent<ShiftTurnoverSearch>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _shiftTurnoverApi = inject(ShiftTurnoverApiService);
  protected _shiftTurnoverTableService = inject(ShiftTurnoverTableService);
  protected _shiftTurnoverBuilder = inject(ShiftTurnoverBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  allShiftTurnovers: any[] = [];
  canCreate = false;
  additionalFilters = [];
  actions: any[] = [];
  startDate!: Date;
  endDate!: Date;
  groups: ShiftGroup[] = [];
  mcGroupId?: string;
  shiftTypes: SelectItem[] = [];
  statusOptions: SelectItem[] = [];
  init = false;
  private _isRefreshing = false;
  refreshSubscription?: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._shiftTurnoverTableService);
    this.initialize();

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  initialize() {
    this.statusOptions = [
      { label: 'Choose', value: null },
      { label: 'New', value: 'N' },
      { label: 'Departing Complete', value: 'DC' },
      { label: 'Resolved', value: 'R' },
    ];
    this._translateService
      .get('MONLOG.SCREEN.SHIFT_TURNOVER')
      .subscribe((label) => {
        this._tabService.updateActiveTabLabel(label);
      });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MC_SHIFT_TURNOVERS.CREATE_MC_TURNOVERS,
    ]);

    this._shiftTurnoverApi.getShiftGroups().subscribe((groups) => {
      this.groups = groups;
      this.mcGroupId = this.groups.find(
        (g) => g.name === 'Monitoring Center'
      )!.id;
      this.applyPreviousData();
      this.applyPreviousFilters();
    });

    this._shiftTurnoverApi.getShiftTypes().subscribe((types) => {
      this.shiftTypes = [
        { label: 'Choose', value: null },
        ...types
          .filter((d) => d.ShiftGroup.name === 'Monitoring Center')
          .map((d) => ({ label: d.name, value: d.id })),
      ];
      this.setupTable();
    });
  }

  setupTable() {
    this.columns = getShiftTurnoverColumns(
      [],
      this.shiftTypes,
      this.statusOptions
    );

    const existingFilters = this._shiftTurnoverTableService.getLastQuery();
    if (!existingFilters || !existingFilters['startDate']) {
      this.goToToday(false);
    } else {
      this.startDate = existingFilters['startDate'];
      this.endDate = existingFilters['endDate'];
      this.dateUpdate();
    }

    this.init = true;
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  goToToday(invoke = true) {
    const today = new Date();
    this.startDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      6,
      0,
      0
    );

    this.endDate = new Date();
    this.endDate.setDate(this.startDate.getDate() + 1);
    this.endDate = new Date(
      this.endDate.getFullYear(),
      this.endDate.getMonth(),
      this.endDate.getDate(),
      5,
      59,
      59
    );
    if (invoke) {
      this.dateUpdate();
    }
  }

  previousDay() {
    this.startDate = new Date(this.startDate.valueOf());
    this.startDate.setDate(this.startDate.getDate() - 1);
    this.startDate.setHours(6);
    this.startDate.setMinutes(0);
    this.startDate.setSeconds(0);

    this.endDate = new Date(this.startDate.valueOf());
    this.endDate.setDate(this.startDate.getDate() + 1);
    this.endDate.setHours(5);
    this.endDate.setMinutes(59);
    this.endDate.setSeconds(59);
    this.dateUpdate();
  }

  nextDay() {
    this.startDate = new Date(this.endDate.valueOf());
    this.startDate.setDate(this.startDate.getDate());
    this.startDate.setHours(6);
    this.startDate.setMinutes(0);
    this.startDate.setSeconds(0);

    this.endDate.setHours(5);
    this.endDate.setMinutes(59);
    this.endDate.setSeconds(59);
    this.endDate.setDate(this.endDate.getDate());
    this.dateUpdate();
  }

  dateUpdate(fromEnd = false) {
    if (this.endDate < this.startDate && fromEnd === false) {
      this.endDate = new Date(this.startDate.valueOf());
      this.endDate.setDate(this.endDate.getDate() + 1);
      this.endDate.setHours(5);
      this.endDate.setMinutes(59);
      this.endDate.setSeconds(59);
    } else if (this.endDate < this.startDate && fromEnd === true) {
      this.startDate = new Date(this.endDate.valueOf());
      this.startDate.setDate(this.startDate.getDate() - 1);
      this.startDate.setHours(6);
      this.startDate.setMinutes(0);
      this.startDate.setSeconds(0);
    }

    this.lazyLoad({ lazy: this._shiftTurnoverTableService.getLastLazyLoad()! });
  }

  changeViewEvent($event: { data: ShiftTurnover }) {
    const tab = this._tabService.buildNewTab(
      MC_ST_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id!
    );
    this._tabService.openTab(tab);
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    if (!this.startDate) {
      this.goToToday(false);
    }

    const query = {
      startDate: this.startDate,
      endDate: this.endDate,
      groupId: this.mcGroupId,
      shiftTypeId: filters.shiftName
        ? (filters.shiftName as FilterMetadata).value
        : null,
      status: filters.statusLabel
        ? (filters.statusLabel as FilterMetadata).value.trim()
        : null,
      incoming: filters.incomingName
        ? (filters.incomingName as FilterMetadata).value.trim()
        : null,
      departing: filters.departingName
        ? (filters.departingName as FilterMetadata).value.trim()
        : null,
    };
    this._shiftTurnoverTableService.setLastQuery(query);
    this.queryNetwork(req, $event, query);
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      let field = $event.lazy.sortField as string;
      if ($event.lazy.sortField === 'incomingName') {
        field = 'incoming';
      } else if ($event.lazy.sortField === 'departingName') {
        field = 'departing';
      } else if ($event.lazy.sortField === 'statusLabel') {
        field = 'status';
      }
      sort[field] =
        $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      this._shiftTurnoverTableService.setLastSortField(
        $event.lazy.sortField as string
      );
      this._shiftTurnoverTableService.setLastSortDirection(
        $event.lazy.sortOrder as number
      );
    } else {
      sort['shiftDate'] = 'DESC';
      this._shiftTurnoverTableService.setLastSortField('shiftDate');
      this._shiftTurnoverTableService.setLastSortDirection(0);
    }
    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._shiftTurnoverApi
        .queryForShiftTurnovers(req.pageSize, req.page, query, sort)
        .subscribe({
          next: (connection) => {
            const shifts = connection.items;
            this.totalRecords = connection.totalRecords;
            this.elements = [...shifts];
            this.loading = false;
            this._isRefreshing = false;
          },
          error: (error) => {
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'CRM.MESSAGES.ERROR.LOAD_SHIFT_TURNOVERS',
            });
            this.loading = false;
            this._isRefreshing = false;
          },
        });
    }
  }

  newShiftChange() {
    const array = this.groups.filter((g) => g.name === 'Monitoring Center');
    if (array.length > 0) {
      const group = array[0];
      const st = this._shiftTurnoverBuilder.buildNullShiftTurnover(group.id);
      this._shiftTurnoverTableService.setSelected(st);
      const tab = this._tabService.buildNewTab(MC_ST_DETAIL_CONTAINER, true);
      this._tabService.openTab(tab);
    }
  }

  refresh(setLoading = true): void {
    this.lazyLoad(
      { lazy: this._shiftTurnoverTableService.getLastLazyLoad()! },
      setLoading
    );
  }
}
